import type { PropsWithChildren } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { ThemePreferenceContext } from '@app/theme-preference-context'
import {
  applyResolvedTheme,
  getSystemPrefersDark,
  persistThemePreference,
  readThemePreference,
  resolveTheme,
} from '@app/theme-preference'

export function ThemePreferenceProvider({ children }: PropsWithChildren) {
  const [preference, setPreference] = useState(readThemePreference)
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark)
  const resolvedTheme = resolveTheme(preference, systemPrefersDark)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches)
    }

    mediaQuery.addEventListener('change', handleSystemThemeChange)
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange)
  }, [])

  useEffect(() => {
    applyResolvedTheme(preference, resolvedTheme)
    persistThemePreference(preference)
  }, [preference, resolvedTheme])

  const value = useMemo(
    () => ({ preference, resolvedTheme, setPreference }),
    [preference, resolvedTheme],
  )

  return (
    <ThemePreferenceContext.Provider value={value}>
      {children}
    </ThemePreferenceContext.Provider>
  )
}
